import { useMutation, useQuery } from "@apollo/client";
import { Grid, Stepper, Accordion, Title, createStyles, Space, Flex, Tooltip } from "@mantine/core";
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { logPageView} from "../../helpers/useGA";
import { CircleNumber1, CircleNumber2, CircleNumber3, CircleNumber4, CircleCheck } from 'tabler-icons-react';
import { CHANGE_STAGE, CURRENT_USER } from "../../queries/auth";
import { ADD_LOG } from "../../queries/user-analytics"; // added fro tracking data
import "./JourneyPage.css"

const useStyles = createStyles((theme) => ({
    root: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: theme.radius.sm,
    },

    item: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: '1px solid transparent',
        position: 'relative',
        zIndex: 0,
        transition: 'transform 150ms ease',

        '&[data-active]': {
            transform: 'scale(1.03)',
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
            boxShadow: theme.shadows.md,
            borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
            borderRadius: theme.radius.md,
            zIndex: 1,
        },
    },

    chevron: {
        '&[data-rotate]': {
            transform: 'rotate(-180deg)',
        },
    },

    separator: {
        borderTop: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4]}`,
        borderRadius: theme.radius.xl,
        backgroundColor: 'inherit',
    },

    separatorActive: {
        borderWidth: 0,
        backgroundImage: theme.fn.linearGradient(45, theme.colors.blue[6], theme.colors.cyan[6]),
    },
}));


export default function JourneyPage(props) {
    const { loading, error, data, refetch } = useQuery(CURRENT_USER);
    const location = useLocation();
    const [active, setActive] = useState(1);
    const { classes } = useStyles();
    const [value, setValue] = useState(["step1"]);
    const [userid, setUserid] = useState(null);
    const isMobile = useMediaQuery('(max-width: 1090px)');
    // added for tracking user data
    const [logEventMutation] = useMutation(ADD_LOG, {
        onCompleted: ({ addLog }) => {
            // Access the HTTP response directly
            const { status, message } = addLog;
            if (status === "success") {
                // logger.info(`Event logged successfully: ${message}`);
                // console.log("Event logged successfully:", message);
            } else {
                console.error("Failed to log event:", message);
            }
        },
        onError: (error) => {
            console.error("Error logging event:", error.message);
        }
    });

    const logEvent = (eventType, additionalData, _userid) => {
        const eventData = {
            userid: Number(_userid),
            event_type: eventType,
            timestamp: new Date().toISOString(),
            additional_data: JSON.stringify(additionalData)

        };

        logEventMutation({ variables: eventData });
    };
    const logEventCalledRef = useRef(false);
    // Track whether we've already seeded the initial open-step from the user's
    // learnstage. Without this, the effect below would re-set `value` on every
    // render (logEvent changes identity each render) and snap any panel the
    // user just opened back shut.
    const initialStepSeededRef = useRef(false);

    useEffect(() => {
        refetch()
    }, [refetch])
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (data && data.currentUser) {
                logEvent('WINDOW CLOSE', { message: 'User closed the window or navigated away' }, data.currentUser.userid);
            }
            event.returnValue = ''; // Necessary for some browsers to show the confirmation dialog
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [data]);


    useEffect(() => {
        if (loading) {
            console.log('Loading user...');
        }
        else if (error) {
            setActive(0);
            if (!initialStepSeededRef.current) { setValue(['step1']); initialStepSeededRef.current = true; }
        }
        else {
            // console.log("data: ", data);
            if (data.currentUser === null) {
                setActive(0);
                if (!initialStepSeededRef.current) { setValue(['step1']); initialStepSeededRef.current = true; }
            } else {
                setUserid(data.currentUser.userid); setActive(data.currentUser.learnstage);
                if (!initialStepSeededRef.current) {
                    setValue(["step" + (data.currentUser.learnstage + 1)]);
                    initialStepSeededRef.current = true;
                }
                if (logEventCalledRef.current === false)
                {
                    logEvent('JourneyPage', data.currentUser, data.currentUser.userid); // added for tracking user data
                    logEventCalledRef.current = true;
                    const pageTitle = "Journey Page"
                    document.title = pageTitle; // Update document title
                    logPageView(location.pathname + location.search, pageTitle); // Ensure this function is properly set up in your GA helper
                }
                
            }
        }
    }, [loading, error, data, location.pathname, location.search])

    const [changeStage] = useMutation(CHANGE_STAGE, {
        onCompleted: ({ changeStage }) => {
            if (changeStage.response) {
                // console.log(changeStage.response)
            }
        }
    })
    return (
        <Flex justify="center" align="center" direction="column">
            <Title p="xl" m="xl" align="center">Learn to become a Trade Expert from Today!</Title>
            <Grid gutter="20px">
                {!isMobile && <Grid.Col sx={{ height: "15vh" }}>
                    <Stepper classNames={classes} active={active} orientation="horizontal" style={{ margin: 'auto', width: '80%', backgroundColor: "inherit" }}>
                        <Stepper.Step label="Step 1" description="Create an account" />
                        <Stepper.Step label="Step 2" description="Try paper trading" />
                        <Stepper.Step label="Step 3" description="Connect with APIs" />
                        <Stepper.Step label="Step 4" description="Participate in contest" />
                    </Stepper>
                </Grid.Col>}
                <Grid.Col>
                    <Accordion
                        multiple
                        value={value}
                        onChange={setValue}
                        variant="contained"
                        sx={{ margin: 'auto', width: '80%', backgroundColor: "inherit", minWidth: "75vw" }}
                        classNames={classes} className={classes.root}
                    >
                        <Accordion.Item value="step1" p="sm">
                            <Accordion.Control icon={active > 0 ? <CircleCheck size={30} color={'#2d863c'} /> : <CircleNumber1 size={30} />}>
                                <b>Step 1 - Register an account</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <div className="accordion-lbl-small">
                                    Task
                                </div>
                                <div className="can-desc">
                                    <h3>Register an account with mobile</h3>
                                    to experience the journey
                                </div>
                                <br />
                                <div className="accordion-lbl-small">
                                    Details
                                </div>
                                <div className="can-desc">
                                    - This will take you not more than 5 minutes<br />
                                    - If you encountered any difficulties, <b>contact NUS FinTech Lab for help</b>
                                </div>
                                <br />
                                <div className="text-center">
                                    {data && data.currentUser ? (
                                        <Tooltip label="You already have an account" withArrow>
                                            <span
                                                className="btn-cta-can finlab-lite-a inverse"
                                                style={{ opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }}
                                                aria-disabled="true"
                                            >
                                                Register Now
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Link to="../register" className="btn-cta-can finlab-lite-a inverse" tabIndex="0">
                                            Register Now
                                        </Link>
                                    )}
                                    <div className="cta-extra-text text-center"></div>
                                </div>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="step2" p="sm">
                            <Accordion.Control icon={active > 1 ? <CircleCheck size={30} color={'#2d863c'} /> : <CircleNumber2 size={30} />}>
                                <b>Step 2 - Try paper trading</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <div className="accordion-lbl-small">
                                    Task
                                </div>
                                <div className="can-desc">
                                    <h3>Try paper trading </h3>
                                    to gain market experience in our simulated market
                                </div>
                                <br />
                                <div className="accordion-lbl-small">
                                    Details
                                </div>
                                <div className="can-desc">
                                    - Try to place different types of orders, and see how they are executed in the market<br />
                                    - Check your initial wallet in Asset page, <b>top up your balance if you need</b>
                                </div>
                                <br />
                                <div className="text-center">
                                    {data && data.currentUser ? (
                                        <Link
                                            to="../trade/btcusd"
                                            className="btn-cta-can finlab-lite-a inverse"
                                            tabIndex="0"
                                            onClick={() => changeStage({ variables: { userid: userid, stage: 2 } })}
                                        >
                                            Try Now
                                        </Link>
                                    ) : (
                                        <Tooltip label="Please login first" withArrow>
                                            <span
                                                className="btn-cta-can finlab-lite-a inverse"
                                                style={{ opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }}
                                                aria-disabled="true"
                                            >
                                                Try Now
                                            </span>
                                        </Tooltip>
                                    )}
                                    <div className="cta-extra-text text-center"></div>
                                </div>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="step3" p="sm">
                            <Accordion.Control icon={active > 2 ? <CircleCheck size={30} color={'#2d863c'} /> : <CircleNumber3 size={30} />}>
                                <b>Step 3 - Connect with APIs</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <div className="accordion-lbl-small">
                                    Task
                                </div>
                                <div className="can-desc">
                                    <h3>Connect with the website via APIs</h3>
                                    to start your coding journey and develop your own trading bot
                                </div>
                                <br />
                                <div className="accordion-lbl-small">
                                    Details
                                </div>
                                <div className="can-desc">
                                    - Follow the tutorial provided and play around with your local environment<br />
                                    - If you encountered any difficulties, <b>contact NUS FinTech Lab for help</b>
                                </div>
                                <br />
                                <div className="text-center">
                                    {data && data.currentUser ? (
                                        <Link
                                            to="../tutorial"
                                            className="btn-cta-can finlab-lite-a inverse"
                                            tabIndex="0"
                                            onClick={() => changeStage({ variables: { userid: userid, stage: 3 } })}
                                        >
                                            Learn Now
                                        </Link>
                                    ) : (
                                        <Tooltip label="Please login first" withArrow>
                                            <span
                                                className="btn-cta-can finlab-lite-a inverse"
                                                style={{ opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }}
                                                aria-disabled="true"
                                            >
                                                Learn Now
                                            </span>
                                        </Tooltip>
                                    )}
                                    <div className="cta-extra-text text-center"></div>
                                </div>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="step4" p="sm">
                            <Accordion.Control icon={active > 3 ? <CircleCheck size={30} color={'#2d863c'} /> : <CircleNumber4 size={30} />}>
                                <b>Step 4 - Join our contest</b>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <div className="accordion-lbl-small">
                                    Task
                                </div>
                                <div className="can-desc">
                                    <h3>Contest every week</h3>
                                    to compete and see your ranking!
                                </div>
                                <br />
                                <div className="accordion-lbl-small">
                                    Details
                                </div>
                                <div className="can-desc">
                                    - Compete with other well-developed trading bots and try to profit from the fierece market<br />
                                    - Try your best to get a high return, but remember <b>the ranking will be calculated based on multiple metrics</b>
                                </div>
                                <br />
                                <div className="text-center">
                                    {data && data.currentUser ? (
                                        <Link
                                            to="../contest"
                                            className="btn-cta-can finlab-lite-a inverse"
                                            tabIndex="0"
                                            onClick={() => changeStage({ variables: { userid: userid, stage: 4 } })}
                                        >
                                            Join Now
                                        </Link>
                                    ) : (
                                        <Tooltip label="Please login first" withArrow>
                                            <span
                                                className="btn-cta-can finlab-lite-a inverse"
                                                style={{ opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }}
                                                aria-disabled="true"
                                            >
                                                Join Now
                                            </span>
                                        </Tooltip>
                                    )}
                                    <div className="cta-extra-text text-center"></div>
                                </div>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                    {isMobile && <Space h="xl"></Space>}
                    {isMobile && <Space h="xl"></Space>}
                    {isMobile && <Space h="xl"></Space>}
                    {isMobile && <Space h="xl"></Space>}
                </Grid.Col>
            </Grid>
        </Flex>
    )
}
